import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// iter65 — Self-hosted Google OAuth callback handler.
// Reads ?code= & ?state= from the URL (planted by Google's redirect back
// to {origin}/auth/google/callback), POSTs them to the backend's
// /api/auth/google/exchange, stores the returned JWT in localStorage,
// refreshes the canonical /auth/me, and navigates to the
// originally-requested route (e.g. /dashboard/new when the user clicked
// "Continue with Google" from the Create-Memorial / Register page).
//
// All legacy Emergent-managed-auth code paths (hash-fragment session_id)
// were removed in iter65 per product owner request.
export default function AuthCallback() {
  const nav = useNavigate();
  const { setUser, refreshUser } = useAuth();
  const done = useRef(false);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const err = params.get('error');

    if (err) {
      setErrMsg(`Google sign-in was cancelled or denied (${err}).`);
      // Small delay so the user can read the message before the redirect.
      setTimeout(() => nav('/login?error=google_cancelled', { replace: true }), 1500);
      return;
    }

    if (!code || !state) {
      // No OAuth payload — most likely a stale link / page refresh.
      nav('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const { data } = await api.post('/auth/google/exchange', {
          code,
          state,
          origin: window.location.origin,
        });

        // 1) Persist the JWT BEFORE anything else so api.js's Bearer
        //    interceptor will start attaching it on the very next request.
        if (data?.token) {
          try { localStorage.setItem('token', data.token); } catch { /* noop */ }
        }

        // 2) Seed the AuthContext synchronously so ProtectedRoute renders
        //    children instead of bouncing to /login while loadMe is in flight.
        if (data?.user) setUser(data.user);

        // 3) Re-fetch the canonical /auth/me so we pick up the freshest
        //    `profile_completed` / `account_type` flags. Best-effort —
        //    if it fails we still have the user object from step 2.
        try { await refreshUser(); } catch { /* noop */ }

        // 4) Navigate to the originally-intended route. The backend
        //    already enforces an allow-list on `return_to` so an
        //    attacker cannot redirect us off-site.
        const returnTo = data?.return_to || '/dashboard';
        window.history.replaceState({}, '', returnTo);
        nav(returnTo, { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || 'unknown';
        setErrMsg(`We couldn't complete the Google sign-in: ${detail}`);
        setTimeout(() => nav('/login?error=google', { replace: true }), 2200);
      }
    })();
  }, [nav, setUser, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-sm px-6" data-testid="auth-callback-status">
        <div className="ml-overline">MyLeaf Legacy</div>
        <div className="font-serif text-2xl mt-2 text-[hsl(var(--primary))]">
          {errMsg ? 'Sign-in could not complete' : 'Securing your session…'}
        </div>
        {errMsg && (
          <p className="mt-3 text-sm text-[hsl(var(--destructive))]" data-testid="auth-callback-error">
            {errMsg}
          </p>
        )}
      </div>
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Initiates the Google OAuth round-trip. `returnTo` is the in-app path the
// caller wants the user back on AFTER Google bounces them home — e.g.
// `/dashboard/new` when launched from the Create-Memorial flow.
//
// iter70 — Anchor on `new URL(env).origin` (with `window.location.origin`
// fallback) so the URL is always ABSOLUTE and never carries a stray
// path component from a misconfigured `REACT_APP_BACKEND_URL` env var.
export function googleSignIn(returnTo) {
  let base = '';
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (raw) {
    try { base = new URL(raw).origin; } catch { base = ''; }
  }
  if (!base && typeof window !== 'undefined' && window.location?.origin) {
    base = window.location.origin.replace(/\/+$/, '');
  }
  const origin = (typeof window !== 'undefined' && window.location?.origin)
    ? window.location.origin.replace(/\/+$/, '')
    : '';
  const rt = encodeURIComponent(returnTo || '/dashboard');
  const url = `${base}/api/auth/google/start?origin=${encodeURIComponent(origin)}&return_to=${rt}`;
  // Use assign(), not href =, so any router that intercepts href changes
  // (history listeners, link clicks) cannot mistake it for an in-app nav.
  window.location.assign(url);
}
